import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification } from 'ant-design-vue';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import ConfiguratorService from '@/services/ConfiguratorService';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import OfferItem from '@/models/OfferItem';
import { checklistFieldsWithErrors } from '@/helpers/OfferHelper';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { MultipositionProduct } from '@/interfaces/components/projectNew/MultipositionProduct';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import { updateRowNumbersForOfferItemsAndTitles } from '@/helpers/OfferItemTableHelper';
import i18n from '@/i18n';
import store from '@/store';

/* === Props & Emits === */

export default /*#__PURE__*/_defineComponent({
  __name: 'AddOfferItems',
  props: {
    activeItemRows: { type: Array as PropType<ProductRowItem[]>, required: true },
    projectId: { type: String, required: true },
    offerId: { type: String as PropType<string | null>, required: true },
    clientsPaymentTypeId: { type: String as PropType<string | null>, default: null },
    offerItemIdsToBeDeleted: { type: Array as PropType<string[]>, default: () => [] },
    offerItemIdsForUpdatingRowNumbers: { type: Array as PropType<any[]>, default: () => [] },
    isUpdating: { type: Boolean, required: true },
    productSlotRows: { type: Function as PropType<() => any[]>, required: false },
    formFieldErrorsExist: { type: Boolean, required: true },
    configuration: { type: Object as PropType<{ isGlobalButton: boolean }>, required: false },
    productFormId: { type: String, required: false },
    popupContext: { type: Object as PropType<any>, required: true },
},
  emits: ['offerIdResolved', EventBusEvents.validateFormFields],
  setup(__props, { emit }) {

const props = __props;




const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === State === */
const updatedOfferId = ref(props.offerId);
const eventFinishedCallback = ref<(() => void) | null>(null);
const eventFinished = ref(false);
const isFetchingId = ref(false);
const loadingOverlay = new LoadingOverlayHelper(componentProxy, {});
let combinationImages: Record<number, { image: string; type: 'sestav' | 'kotni' }> = {};

const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

const isGlobalButton = computed(() => props.configuration?.isGlobalButton);

/* === Draft Autosave === */
watch(
    () => props.activeItemRows,
    async (newValue: ProductRowItem[]) => {
        if (!newValue.length || newValue.some((row) => row.isUpdatingData)) {
            return;
        }
        try {
            if (!updatedOfferId.value) {
                if (isFetchingId.value) return;
                isFetchingId.value = true;
                await getOfferId();
                isFetchingId.value = false;
            }
            if (!updatedOfferId.value) return;

            const contextKey = props.popupContext.contextKey || 'DEFAULT_FLOW';
            const offerId = updatedOfferId.value;

            const payloads: MultipositionProduct[] = await Promise.all(
                newValue.map((row) => preparePayloadForOfferItems(row)),
            );

            const fullDraftState = {
                ...props.popupContext,
                restoredRowData: payloads,
                isRestored: true,
            };

            if (payloads.length > 0) {
                ConfiguratorService.autoSaveDraft(offerId, contextKey, fullDraftState);
            }
        } catch (e) {
            isFetchingId.value = false;
            console.error('[Autosave] Failed', e);
        }
    },
    { deep: true },
);

const multipositionButtonText = computed(() => {
    if (props.configuration?.isGlobalButton) {
        return props.isUpdating ? i18n.t('Save all') : i18n.t('Add all');
    }

    return props.isUpdating ? i18n.t('Save') : i18n.t('Add');
});

/* === Offer Submission === */
function errorsHaveValidWarranty(checklistErrors: ChecklistField[]) {
    if (!checklistErrors?.length) {
        return true;
    }

    const invalidWarranty = checklistErrors.some((checklist: ChecklistField) => {
        if (checklist.checklistStringField) {
            return !checklist.checklistStringField.validWarranty;
        }
    });

    return !invalidWarranty;
}

async function updateOrAddItemsToOffer() {
    if (updatedOfferId.value) {
        ConfiguratorService.clearAllOfferDrafts(updatedOfferId.value);
    }

    loadingOverlay.start();
    eventFinishedCallback.value = null;
    emit(EventBusEvents.validateFormFields, props.activeItemRows);

    await new Promise<void>((resolve) => {
        eventFinishedCallback.value = resolve;

        if (eventFinished.value) {
            eventFinished.value = false;
            resolve();
        }
    });

    if (props.formFieldErrorsExist) {
        loadingOverlay.stop();
        return;
    }

    const segmentedComponent = componentProxy.$parent?.$refs.segmentedMultiposition as any;
    const segmentedInstances = Array.isArray(segmentedComponent)
        ? segmentedComponent
        : segmentedComponent
        ? [segmentedComponent]
        : [];

    combinationImages = {};
    const removedConnectionIdsToDelete = new Set<number>();
    for (const instance of segmentedInstances) {
        if (typeof instance.getCombinationImages === 'function') {
            const images = instance.getCombinationImages();
            Object.assign(combinationImages, images);
        }
        if (typeof instance.getRemovedConnectionIds === 'function') {
            const ids = instance.getRemovedConnectionIds();
            ids.forEach((id: number) => removedConnectionIdsToDelete.add(id));
        }
    }

    const stillUsedConnectionIds = new Set(
        props.activeItemRows
        .map((row) => row.connection)
        .filter((c): c is number => c !== null && c !== undefined),
    );
    const finalRemovedConnectionIds = Array.from(removedConnectionIdsToDelete).filter(
        (id) => !stillUsedConnectionIds.has(id),
    );

    try {
        await deletePendingOfferItems();
        await updateOrCreateOfferItems();
        await uploadCombinationImages();
        await deleteCombinationImages(finalRemovedConnectionIds);
    } catch (error) {
        if (error && (error as any).message) {
            notification.error({
                message: i18n.t('Dogodila se greška') as string,
                description: (error as Error).message,
            });
        }
        loadingOverlay.stop();
        return;
    }

    if (props.isUpdating) {
        try {
            await updateOfferItemRowNumbers();
        } catch (error) {
            loadingOverlay.stop();
            notification.error({
                message: i18n.t('Dogodila se greška') as string,
                description: i18n.t('Error updating row numbers') as string,
            });
            return;
        }
    }

    EventBus.$emit(EventBusEvents.updateProject, {
        newOfferId: null,
        redirectTo: null,
        shouldUpdateOffer: true,
        shouldUpdateRowNumbers: props.activeItemRows.length > 1 && !props.isUpdating,
    });

    if (!props.configuration?.isGlobalButton) {
        EventBus.$emit(EventBusEvents.removeSavedMultipositionSegment, props.productFormId);

        notification.success({
            message: props.isUpdating
                ? (i18n.t('Segment changed successfully!') as string)
                : (i18n.t('Proizvodi uspješno dodani!') as string),
            description: '',
        });
    } else {
        notification.success({
            message: props.isUpdating
                ? (i18n.t('Proizvodi uspješno promijenjeni!') as string)
                : (i18n.t('Proizvodi uspješno dodani!') as string),
            description: '',
        });
        EventBus.$emit(EventBusEvents.toggleEditingForProductsInOffer);
        EventBus.$emit(EventBusEvents.closePopup);
    }

    for (const instance of segmentedInstances) {
        if (typeof instance.clearRemovedConnectionIds === 'function') {
            instance.clearRemovedConnectionIds();
        }
    }

    loadingOverlay.stop();
}

async function updateOfferItemRowNumbers() {
    try {
        await updateRowNumbersForOfferItemsAndTitles(props.offerItemIdsForUpdatingRowNumbers);
    } catch (e) {
        return Promise.reject();
    }
}

async function preparePayloadForOfferItems(activeItemRow: ProductRowItem) {
    const activeProductFormId = activeItemRow.activeProductFormId;
    let productFormValues = getProductFormValues(activeProductFormId);
    const hasErrors = checklistFieldsWithErrors(activeItemRow).length > 0;

    // @ts-ignore
    productFormValues = Object.keys(productFormValues).map((key) => {
        // @ts-ignore
        const value = productFormValues[key];
        if (typeof value === 'object' && value !== null) {
            return value; // Pass the object as it is
        }
        return { id: key, value }; // Convert primitive values to { id, value } objects
    });

    if (productFormValues == null) {
        return Promise.reject();
    }

    const checklistErrors = checklistFieldsWithErrors(activeItemRow);
    const hasValidWarranty = errorsHaveValidWarranty(checklistErrors);
    const offerItemId = activeItemRow.offerItem != null ? activeItemRow.offerItem.id : null;

    const payload: any = {
        formData: productFormValues,
        hasErrors,
        production: activeItemRow.production,
        offerItemType: activeItemRow?.offerItem?.offerItemType ?? 'product',
        validWarranty: hasValidWarranty,
        activeProductFormId: activeItemRow.productFormId,
        connection: activeItemRow.connection,
    };

    if (offerItemId) {
        payload.id = offerItemId;
    }

    return Promise.resolve(payload);
}

async function onAddItemsToOffer(payloads: MultipositionProduct[]) {
    try {
        await ConfiguratorService.addBulkItemsToOffer(payloads, updatedOfferId.value!);
    } catch (e) {
        loadingOverlay.stop();
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: '',
        });

        return Promise.reject();
    }

    return Promise.resolve();
}

async function onUpdateItemsInOffer(payloads: MultipositionProduct[]) {
    try {
        await ConfiguratorService.updateBulkItemsInOffer(payloads, updatedOfferId.value!);
    } catch (e) {
        loadingOverlay.stop();
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: '',
        });

        return Promise.reject();
    }

    return Promise.resolve();
}

async function deletePendingOfferItems() {
    const promises = [];

    for (const offerItemId of props.offerItemIdsToBeDeleted) {
        promises.push(OfferItem.deleteExisting(offerItemId));
    }

    try {
        await Promise.all(promises);
    } catch {
        return Promise.reject();
    }

    return Promise.resolve();
}

async function getOfferId() {
    let validOfferId;

    try {
        validOfferId = await ConfiguratorService.getValidOfferId(
            props.projectId,
            updatedOfferId.value,
            props.clientsPaymentTypeId,
        );
        updatedOfferId.value = validOfferId;
        emit('offerIdResolved', validOfferId);
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: i18n.t('Error getting offer ID'),
        });
        loadingOverlay.stop();
        return Promise.reject();
    }
}

async function deleteCombinationImages(connectionIds: number[]): Promise<void> {
  if (!updatedOfferId.value || !connectionIds.length) {
    return;
  }
  const deletions = connectionIds.map((connectionId) =>
    ConfiguratorService.deleteCombinationImage(updatedOfferId.value!, connectionId).catch((e) => {
      console.error('[Delete] Failed for connection', connectionId, e);
    }),
  );
  await Promise.all(deletions);
}

async function uploadCombinationImages(): Promise<void> {
    if (!updatedOfferId.value) {
        console.warn('[Upload] No updatedOfferId, aborting');
        return;
    }

    const entries = Object.entries(combinationImages);

    if (!entries.length) {
        console.warn('[Upload] No entries to upload');
        return;
    }

    const uploads = entries.map(([connectionId, { image, type }]) =>
        ConfiguratorService.uploadCombinationImage(updatedOfferId.value!, Number(connectionId), image, type).catch(
            (e) => {
                console.error('[Upload] Failed for connection', connectionId, e);
            },
        ),
    );
    await Promise.all(uploads);
}

async function updateOrCreateOfferItems() {
    await getOfferId();

    const payloads: MultipositionProduct[] = [];
    const promises = [];
    // Get payloads for all products we gonna add

    try {
        for (const activeItemRow of props.activeItemRows) {
            let promise;

            // Send call for payload preparation and update payloads
            promise = preparePayloadForOfferItems(activeItemRow);
            promises.push(
                promise.then((payload: any) => {
                    payloads.push(payload);
                }),
            );
        }

        if (promises.length) {
            await Promise.all(promises);
        }

        await Promise.all([
            onAddItemsToOffer(payloads.filter((payload: MultipositionProduct) => !payload.id)),
            onUpdateItemsInOffer(payloads.filter((payload: MultipositionProduct) => payload.id)),
        ]);
    } catch {
        return Promise.reject();
    }

    return Promise.resolve();
}

function continueExecution() {
    if (eventFinishedCallback.value) {
        eventFinishedCallback.value();
    } else {
        eventFinished.value = true;
    }

    eventFinishedCallback.value = null;
}

function clearDraft() {
    if (updatedOfferId.value) {
        ConfiguratorService.clearAllOfferDrafts(updatedOfferId.value);
    }
}

EventBus.$on(EventBusEvents.formValidationFinished, continueExecution);
EventBus.$on(EventBusEvents.cancelAddProducts, clearDraft);

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.formValidationFinished, continueExecution);
    EventBus.$off(EventBusEvents.cancelAddProducts, clearDraft);
});

return { __sfc: true,props, emit, proxy, componentProxy, updatedOfferId, eventFinishedCallback, eventFinished, isFetchingId, loadingOverlay, combinationImages, getProductFormValues, isGlobalButton, multipositionButtonText, errorsHaveValidWarranty, updateOrAddItemsToOffer, updateOfferItemRowNumbers, preparePayloadForOfferItems, onAddItemsToOffer, onUpdateItemsInOffer, deletePendingOfferItems, getOfferId, deleteCombinationImages, uploadCombinationImages, updateOrCreateOfferItems, continueExecution, clearDraft }
}

})